import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Activity, ClipboardList, FileText, Loader2, ChevronRight, CheckCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  Activity, ClipboardList, FileText,
};

const HealthTestListPage = ({ basePath, backPath, backLabel }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [completedSurveys, setCompletedSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const resolvedBasePath = basePath || `/${user?.user_type || 'client'}`;

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/health-templates`),
      axios.get(`${API}/health-surveys/my`).catch(() => ({ data: [] })),
    ])
      .then(([tplRes, survRes]) => {
        setTemplates(tplRes.data || []);
        setCompletedSurveys(survRes.data || []);
      })
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-[#467322]" size={32} /></div>;

  const getSurveyCount = (tplId) => completedSurveys.filter(s => s.template_id === tplId).length;

  return (
    <div className="max-w-2xl mx-auto space-y-5 animate-fade-in pb-24" data-testid="health-test-list">
      <button onClick={() => navigate(backPath || `${resolvedBasePath}/health`)}
        className="flex items-center gap-2 text-sm text-[#467322] font-semibold hover:text-[#1A2E10] transition-colors"
        data-testid="back-btn">
        <ArrowLeft size={16} /> {backLabel || 'Mi Cuerpo'}
      </button>

      <div>
        <h1 className="text-3xl font-black text-[#1A2E10]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          Test de Salud
        </h1>
        <p className="text-sm text-gray-400 mt-1">{templates.length} evaluaciones disponibles</p>
      </div>

      <div className="space-y-3">
        {templates.map((tpl) => {
          const IconComp = ICON_MAP[tpl.icon] || ClipboardList;
          const count = getSurveyCount(tpl.id);
          const isLegacy = tpl.legacy;

          return (
            <button
              key={tpl.id}
              onClick={() => {
                if (isLegacy) {
                  navigate(`${resolvedBasePath}/health-survey`);
                } else {
                  navigate(`${resolvedBasePath}/health-survey/${tpl.id}`);
                }
              }}
              className="w-full bg-white rounded-2xl border border-gray-100/50 shadow-sm p-4 text-left hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 group"
              data-testid={`test-card-${tpl.slug}`}
            >
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg group-hover:scale-105 transition-transform"
                  style={{ background: `linear-gradient(135deg, ${tpl.color}, ${tpl.color}CC)` }}>
                  <IconComp size={24} className="text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-black text-gray-900 text-sm" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{tpl.name}</p>
                    {count > 0 && (
                      <span className="flex items-center gap-1 text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-100">
                        <CheckCircle size={10} /> Completado
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5 line-clamp-2">{tpl.description}</p>
                  <div className="flex items-center gap-3 mt-2">
                    <span className="text-[10px] font-bold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-lg">
                      {tpl.total_questions} preguntas
                    </span>
                    {tpl.blocks?.length > 0 && (
                      <span className="text-[10px] font-bold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-lg">
                        {tpl.blocks.length} bloques
                      </span>
                    )}
                  </div>
                </div>
                <ChevronRight size={16} className="text-gray-300 group-hover:text-[#467322] transition-colors flex-shrink-0" />
              </div>
            </button>
          );
        })}
      </div>

      {templates.length === 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
          <ClipboardList size={40} className="text-gray-200 mx-auto mb-3" />
          <p className="font-bold text-gray-500">No hay evaluaciones disponibles</p>
        </div>
      )}
    </div>
  );
};

export default HealthTestListPage;
